import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getUserById } from "@/shared/data";
import type { User } from "@/shared/types";
import type { AuthContextValue } from "./auth-types";

const AuthContext = createContext<AuthContextValue | null>(null);
const STORAGE_KEY = "pikpok-auth-user-id";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    // One-shot hydration from localStorage — runs once after mount, no cascading renders.
    const id = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (id) {
      const u = getUserById(id);
      if (u) setCurrentUser(u);
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    currentUser,
    async signIn(email, password) {
      if (!email.trim() || !password.trim()) throw new Error("Email and password required");
      const u = getUserById("you")!;
      setCurrentUser(u);
      localStorage.setItem(STORAGE_KEY, u.id);
    },
    async signUp({ email, password, username }) {
      if (!email.trim() || !password.trim() || !username.trim()) throw new Error("All fields required");
      const base = getUserById("you")!;
      const u = { ...base, username };
      setCurrentUser(u);
      localStorage.setItem(STORAGE_KEY, base.id);
    },
    signOut() {
      setCurrentUser(null);
      localStorage.removeItem(STORAGE_KEY);
    },
  }), [currentUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
